import { useQueryClient, type QueryKey } from "@tanstack/react-query";
import { useEffect, useEffectEvent, useState } from "react";
import type { SessionDetail, Turn, TurnEvent, TurnStep } from "../../../shared/api.ts";
import { api } from "./api.ts";
import { openEventStream, type EventStream } from "./http.ts";
import { isActiveStatus, patchTurn, setReplyDraft, sortSteps, upsertStep, upsertTurn } from "./turns.ts";

type EventData = { [K in TurnEvent["event"]]: Extract<TurnEvent, { event: K }>["data"] };

const CLOSED = 2;

/**
 * Follows `GET /api/turns/:id/events` while a turn is queued or running and applies
 * `snapshot`, `turn`, `step`, `draft`, and `done` to the cached `SessionDetail`.
 * A dropped stream is reopened with backoff; the server always starts with a full snapshot.
 *
 * @param queryKey - Cache entry that holds the session.
 * @param turnId - The active turn, or null when nothing runs.
 * @param onFinished - Called once when the turn ends, to refetch the session and the list.
 */
export function useTurnStream(queryKey: QueryKey, turnId: string | null, onFinished: () => void): void {
  const queryClient = useQueryClient();
  const notifyFinished = useEffectEvent(() => onFinished());
  const update = useEffectEvent((change: (detail: SessionDetail) => SessionDetail) => {
    queryClient.setQueryData<SessionDetail>(queryKey, (detail) => (detail ? change(detail) : detail));
  });

  useEffect(() => {
    if (!turnId) return;
    let stream: EventStream | null = null;
    let retryTimer: number | undefined;
    let attempts = 0;
    let stopped = false;

    const finish = () => {
      if (stopped) return;
      stopped = true;
      stream?.close();
      update((detail) => (detail.activeTurnId === turnId ? { ...detail, activeTurnId: null } : detail));
      notifyFinished();
    };

    const parse = <K extends keyof EventData>(_type: K, event: MessageEvent<string>): EventData[K] | null => {
      try {
        return JSON.parse(event.data) as EventData[K];
      } catch {
        return null;
      }
    };

    const connect = () => {
      if (stopped) return;
      const source = openEventStream(api.turnEventsUrl(turnId));
      stream = source;
      source.addEventListener("snapshot", (event) => {
        const turn = parse("snapshot", event);
        if (!turn) return;
        attempts = 0;
        update((detail) => upsertTurn(detail, turn));
        if (!isActiveStatus(turn.status)) finish();
      });
      source.addEventListener("turn", (event) => {
        const turn = parse("turn", event);
        if (!turn) return;
        update((detail) => patchTurn(detail, turn));
      });
      source.addEventListener("step", (event) => {
        const step = parse("step", event);
        if (!step) return;
        update((detail) => upsertStep(detail, turnId, step));
      });
      source.addEventListener("draft", (event) => {
        const draft = parse("draft", event);
        if (!draft) return;
        update((detail) => setReplyDraft(detail, turnId, draft.text));
      });
      source.addEventListener("done", (event) => {
        const turn = parse("done", event);
        if (turn) update((detail) => upsertTurn(detail, turn));
        finish();
      });
      source.onerror = () => {
        if (stopped || source.readyState !== CLOSED) {
          // EventSource retries by itself while it is CONNECTING.
          return;
        }
        const delay = Math.min(15_000, 1_000 * 2 ** attempts);
        attempts += 1;
        retryTimer = window.setTimeout(connect, delay);
      };
    };

    connect();
    return () => {
      stopped = true;
      window.clearTimeout(retryTimer);
      stream?.close();
    };
  }, [turnId]);
}

/** A turn without its steps: what `useTurnStatus` reports when it ends. */
export type TurnHeader = Omit<Turn, "steps">;

/**
 * Follows a turn that is not in the transcript, such as a box 31 generation, on the same events, and returns its
 * latest status, times, phase, and steps, so the page can show what it is doing. A dropped stream is reopened with
 * backoff.
 *
 * @param turnId - The turn, or null when nothing runs.
 * @param onFinished - Called once when the turn ends, with its last state.
 * @returns The latest state the stream sent, or null before the first event.
 */
export function useTurnStatus(turnId: string | null, onFinished: (turn: TurnHeader | null) => void): Turn | null {
  const [latest, setLatest] = useState<{ turnId: string; turn: Turn } | null>(null);
  const notifyFinished = useEffectEvent((turn: TurnHeader | null) => onFinished(turn));

  useEffect(() => {
    if (!turnId) return;
    let stream: EventStream | null = null;
    let retryTimer: number | undefined;
    let attempts = 0;
    let stopped = false;

    // A snapshot and done carry the steps; a turn event carries none, so the steps so far stay.
    const read = (event: MessageEvent<string>): TurnHeader | null => {
      try {
        const { steps, ...turn } = JSON.parse(event.data) as Partial<Turn> & TurnHeader;
        attempts = 0;
        setLatest((current) => ({
          turnId,
          turn: { ...turn, steps: steps ? sortSteps(steps) : current?.turnId === turnId ? current.turn.steps : [] },
        }));
        return turn;
      } catch {
        return null;
      }
    };
    const readStep = (event: MessageEvent<string>) => {
      try {
        const step = JSON.parse(event.data) as TurnStep;
        setLatest((current) => {
          if (!current || current.turnId !== turnId) return current;
          const others = current.turn.steps.filter((existing) => existing.id !== step.id);
          return { turnId, turn: { ...current.turn, steps: sortSteps([...others, step]) } };
        });
      } catch {
        // A step that does not parse is left out; the next snapshot has it.
      }
    };
    const finish = (turn: TurnHeader | null) => {
      if (stopped) return;
      stopped = true;
      stream?.close();
      notifyFinished(turn);
    };

    const connect = () => {
      if (stopped) return;
      const source = openEventStream(api.turnEventsUrl(turnId));
      stream = source;
      source.addEventListener("snapshot", (event) => {
        const turn = read(event);
        if (turn && !isActiveStatus(turn.status)) finish(turn);
      });
      source.addEventListener("turn", (event) => void read(event));
      source.addEventListener("step", readStep);
      source.addEventListener("done", (event) => finish(read(event)));
      source.onerror = () => {
        if (stopped || source.readyState !== CLOSED) return;
        const delay = Math.min(15_000, 1_000 * 2 ** attempts);
        attempts += 1;
        retryTimer = window.setTimeout(connect, delay);
      };
    };

    connect();
    return () => {
      stopped = true;
      window.clearTimeout(retryTimer);
      stream?.close();
    };
  }, [turnId]);

  return latest && latest.turnId === turnId ? latest.turn : null;
}
